import { Card, Flex } from '@brightdrop/bd-ui';
import { useFormat } from '@brightdrop/localization-core';
import { ButtonBase, Typography, useMediaQuery, useTheme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useTranslation } from 'react-i18next';
import { useProviderIntegrationsStatusQuery } from '~/api/generated-api';
import InfoIcon from '~/assets/icons/new/infoIcon.svg?react';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import AdaptiveTooltipDrawer from '~/common/components/tooltips/AdaptiveTooltipDrawer';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import { useOrgId } from '~/common/hooks/useOrgId';
import {
  ENROLLMENT_VIEWS,
  type EnrollmentView,
  type ManageSectionProps,
} from '~/features/integrations/partners/types';

import useStyles from './EnrollmentOverview.styles';
import useOverviewCardStyles from './overview-card.styles';

const TRANSLATION_KEYS = {
  title: 'integrations:account.manage.enrollmentOverview',
  enrolled: 'common:enrolled',
  enrolledTooltip: 'integrations:account.manage.enrolledTooltip',
  errors: 'integrations:account.manage.errors',
  errorsTooltip: 'integrations:account.manage.errorsTooltip',
  pending: 'integrations:account.manage.pending',
  pendingTooltip: 'integrations:account.manage.pendingTooltip',
  eligible: 'common:eligible',
} as const;

type OverviewTile = {
  labelKey: string;
  /** Eligible reuses the partner's own eligibility copy from its Shop card. */
  tooltipKey?: string;
};

const OVERVIEW_TILES: Record<EnrollmentView, OverviewTile> = {
  [ENROLLMENT_VIEWS.ENROLLED]: {
    labelKey: TRANSLATION_KEYS.enrolled,
    tooltipKey: TRANSLATION_KEYS.enrolledTooltip,
  },
  [ENROLLMENT_VIEWS.ERRORS]: {
    labelKey: TRANSLATION_KEYS.errors,
    tooltipKey: TRANSLATION_KEYS.errorsTooltip,
  },
  [ENROLLMENT_VIEWS.PENDING]: {
    labelKey: TRANSLATION_KEYS.pending,
    tooltipKey: TRANSLATION_KEYS.pendingTooltip,
  },
  [ENROLLMENT_VIEWS.ELIGIBLE]: {
    labelKey: TRANSLATION_KEYS.eligible,
  },
};

/**
 * Enrollment counts for the manage page. Which tiles appear comes from the partner config.
 */
const EnrollmentOverview = ({
  partner,
  onSelectTab,
}: ManageSectionProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const { classes: cardClasses } = useOverviewCardStyles();
  useTranslation(['integrations', 'common']);
  const format = useFormat();
  const organizationsId = useOrgId();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { manage, nameKey, shortNameKey, providerCode, shop } = partner;
  const partnerName = translateText(shortNameKey ?? nameKey);
  const { currentData, isError } = useProviderIntegrationsStatusQuery(
    organizationsId
      ? { 'cs-organization': organizationsId, providerCode }
      : skipToken
  );
  const integration =
    !isError && currentData?.success
      ? currentData.result?.integrations.find(
          (entry) => entry.provider.provider.providerCode === providerCode
        )
      : undefined;
  const summary = integration?.integrationStatus?.vehicleEnrollmentStatus;
  const counts: Record<EnrollmentView, number | undefined> = {
    [ENROLLMENT_VIEWS.ENROLLED]: summary?.enrolled,
    [ENROLLMENT_VIEWS.ERRORS]: summary?.errorEnrollment,
    [ENROLLMENT_VIEWS.PENDING]: summary?.pendingEnrollment,
    [ENROLLMENT_VIEWS.ELIGIBLE]: integration?.provider.eligibleVehicleCount,
  };
  const eligibleTooltipKey = shop.counts.find(
    (count) => count.key === ENROLLMENT_VIEWS.ELIGIBLE
  )?.tooltipKey;

  return (
    <Card dataTestId="account-integrations-enrollment-overview">
      <TranslatedLabel
        translationKey={TRANSLATION_KEYS.title}
        typographyVariant="header2_medium"
        className={cardClasses.title}
        component="h2"
      />
      <div className={classes.grid}>
        {manage.enrollmentTiles.map((view) => {
          const tile = OVERVIEW_TILES[view];
          const tooltipKey =
            view === ENROLLMENT_VIEWS.ELIGIBLE
              ? eligibleTooltipKey
              : tile.tooltipKey;
          // Older locales still interpolate the errors copy with `partnerName`.
          const tooltip = tooltipKey
            ? translateText(tooltipKey, {
                partner: partnerName,
                partnerName,
              })
            : undefined;
          const label = translateText(tile.labelKey);
          const value = counts[view];
          const count =
            value === undefined
              ? DEFAULT_EMPTY_VALUE
              : format.number(value, { digits: 0 });
          const opensTab =
            !!onSelectTab &&
            view !== ENROLLMENT_VIEWS.ELIGIBLE &&
            manage.tabs.some((tab) => tab.id === view);
          const tooltipTestId = `account-integrations-enrollment-${view}-tooltip`;
          const aboveOverlay = opensTab ? classes.aboveOverlay : undefined;

          return (
            <div
              key={view}
              className={cx(classes.tile, opensTab && classes.tileOpensTab)}
            >
              {opensTab && (
                <ButtonBase
                  className={classes.tileOverlay}
                  aria-label={[`${label}: ${count}`, tooltip]
                    .filter(Boolean)
                    .join('. ')}
                  onClick={() => onSelectTab(view)}
                  data-testid={`account-integrations-enrollment-${view}-open`}
                />
              )}
              <Flex className={cx(classes.labelRow, aboveOverlay)}>
                <Typography
                  variant="small_body_medium"
                  className={cardClasses.secondary}
                  aria-hidden={opensTab || undefined}
                >
                  {label}
                </Typography>
                {tooltip && (
                  <AdaptiveTooltipDrawer
                    isMobile={isMobile}
                    placement="top"
                    ariaLabel={tooltip}
                    icon={<InfoIcon />}
                    content={tooltip}
                    drawerTitle={label}
                    className={cardClasses.tooltipIcon}
                    dataTestId={tooltipTestId}
                  />
                )}
              </Flex>
              <Typography
                variant="display2_medium"
                className={cx(classes.count, classes[view], aboveOverlay)}
                aria-hidden={opensTab || undefined}
                data-testid={`account-integrations-enrollment-${view}`}
              >
                {count}
              </Typography>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default Object.assign(EnrollmentOverview, {
  sectionId: 'enrollment-overview',
});
